/* Công nợ & Thanh toán v2.4 — Hide settled by default (READ-ONLY).
 *
 * HARD RULE: dùng lại stream có sẵn của web/FinancePage, KHÔNG tạo hệ dòng mới và KHÔNG có view "Tất cả":
 *   - main (Dòng chính): rental-costume, making-props, photoshoot
 *   - side (Dòng phụ):  rental-gun, order
 *
 * Khách dùng chung định danh, nhưng debt/tài chính tách tuyệt đối theo dòng.
 * Tổng nợ chỉ tính từ TASK (không cộng finance vào debt). Finance entries
 * chỉ dùng cho danh sách review/CK chưa match, cũng lọc theo dòng đã chọn.
 * Bản ghi không xác định được dòng → KHÔNG cộng vào tổng; hiện ở panel cảnh báo.
 * v2.4: mặc định “Đang cần thu”; task đã tất toán ẩn mặc định và chuyển vào tab “Đã tất toán”.
 *
 * Mọi field mới (real_name / bank_transfer_names / payer_name / payment_method /
 * match_status / total_amount / deposit_amount / remaining_amount /
 * payment_received) là OPTIONAL — đọc qua cast mềm, thiếu thì ẩn / hiện "Chưa có".
 */
import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import {
  Customer, Task, FinanceEntry, formatMoney, SectionHeader,
} from "../../AppStatic";

type Stream = "main" | "side";
type StatusTab = "needs-collect" | "no-deposit" | "partial" | "unmatched-ck" | "vip" | "settled";

const STREAMS: Array<{ key: Stream; label: string; hint: string }> = [
  { key: "main", label: "Dòng chính", hint: "Làm đồ · Cho thuê đồ · Chụp hình" },
  { key: "side", label: "Dòng phụ",   hint: "Cho thuê mô hình súng · Order" },
];

const STATUS_TABS: Array<{ key: StatusTab; label: string }> = [
  { key: "needs-collect", label: "Đang cần thu" },
  { key: "no-deposit",    label: "Chưa cọc" },
  { key: "partial",       label: "Còn thiếu" },
  { key: "unmatched-ck",  label: "CK cần kiểm tra" },
  { key: "vip",           label: "VIP / Ưu tiên" },
  { key: "settled",       label: "Đã tất toán" },
];

// ===== Stream resolver
// Task.project_id trong DB thật là UUID của task_projects.id, không phải key.
// Vì vậy phải dùng /tasks/projects để map:
//   task.project_id UUID → task_projects.key/revenue_stream → main/side.
// Vẫn giữ fallback theo key để tương thích nếu API trả project_id dạng key.
type TaskProject = {
  id: string;
  key?: string | null;
  name?: string | null;
  project_group?: string | null;
  revenue_stream?: string | null;
};

const MAIN_PROJECT_KEYS = new Set(["rental-costume", "making-props", "photoshoot", "archive-making", "archive-rental"]);
const SIDE_PROJECT_KEYS = new Set(["rental-gun", "order"]);

const PRIORITY_TAGS = ["ưu tiên", "uu tien", "cọc cao", "coc cao", "khách gấp", "khach gap"];
const SHOW_TAGS = new Set([
  "ưu tiên", "uu tien", "vip", "cọc cao", "coc cao",
  "khách mới", "khach moi", "khách cũ", "khach cu",
  "khách quen", "khach quen", "khách gấp", "khach gap",
]);

function norm(s: unknown): string {
  return String(s ?? "").trim().toLowerCase();
}
function isVip(c?: Customer | null): boolean {
  if (!c) return false;
  const score = Number((c as any).vip_score ?? 0) || 0;
  if (score > 0) return true;
  const tags = ((c as any).tags as string[] | null | undefined) ?? [];
  return tags.some((t) => norm(t) === "vip");
}
function isPriority(c?: Customer | null): boolean {
  if (!c) return false;
  const tags = ((c as any).tags as string[] | null | undefined) ?? [];
  return tags.some((t) => PRIORITY_TAGS.includes(norm(t)));
}

function projectToStream(p: TaskProject | undefined | null): Stream | null {
  if (!p) return null;

  const rs = norm(p.revenue_stream);
  if (rs === "main" || rs === "side") return rs;

  const key = norm(p.key);
  if (MAIN_PROJECT_KEYS.has(key)) return "main";
  if (SIDE_PROJECT_KEYS.has(key)) return "side";

  return null;
}

function rawProjectIdToStream(rawProjectId: unknown, projectStreamById: Map<string, Stream | null>): Stream | null {
  const pid = String(rawProjectId ?? "").trim();
  if (!pid) return null;

  // DB thật dùng UUID trong tasks.project_id / finance_entries.project_id.
  const byId = projectStreamById.get(pid);
  if (byId === "main" || byId === "side") return byId;

  // Fallback nếu API/CSV cũ đưa key thay vì UUID.
  const key = norm(pid);
  if (MAIN_PROJECT_KEYS.has(key)) return "main";
  if (SIDE_PROJECT_KEYS.has(key)) return "side";

  return null;
}

function taskStream(
  t: Task | undefined | null,
  projectStreamById: Map<string, Stream | null>,
): Stream | null {
  if (!t) return null;

  const byProject = rawProjectIdToStream((t as any).project_id, projectStreamById);
  if (byProject === "main" || byProject === "side") return byProject;

  const rs = norm((t as any).revenue_stream);
  if (rs === "main" || rs === "side") return rs;

  return null;
}

function financeStream(
  e: FinanceEntry,
  taskStreamMap: Map<string, Stream | null>,
  projectStreamById: Map<string, Stream | null>,
): Stream | null {
  // 1) Ưu tiên revenue_stream nếu hợp lệ.
  const rs = norm((e as any).revenue_stream);
  if (rs === "main" || rs === "side") return rs;

  // 2) Suy theo task liên kết.
  if (e.task_id) {
    const s = taskStreamMap.get(e.task_id);
    if (s === "main" || s === "side") return s;
  }

  // 3) Suy theo finance_entries.project_id, hỗ trợ cả UUID và key.
  return rawProjectIdToStream((e as any).project_id, projectStreamById);
}

function taskAmounts(t: Task | undefined | null) {
  const total     = Number((t as any)?.total_amount ?? 0) || 0;
  const deposit   = Number((t as any)?.deposit_amount ?? 0) || 0;
  const remainingRaw = (t as any)?.remaining_amount;
  const remaining = remainingRaw == null ? Math.max(total - deposit, 0) : Math.max(Number(remainingRaw) || 0, 0);
  const paymentReceived = Boolean((t as any)?.payment_received);
  const paidEnough = paymentReceived || (total > 0 && remaining <= 0);
  const noDeposit  = total > 0 && deposit <= 0 && !paidEnough;
  const partial    = total > 0 && deposit > 0 && remaining > 0 && !paidEnough;
  return { total, deposit, remaining, paymentReceived, paidEnough, noDeposit, partial };
}

function isFinanceLinked(e: FinanceEntry): boolean {
  return Boolean((e as any).customer_id || (e as any).task_id);
}
function isIncomeLike(e: FinanceEntry): boolean {
  const kind = norm((e as any).kind);
  if (kind === "expense" || kind === "chi") return false;
  return (Number((e as any).amount) || 0) > 0;
}
function isLikelyBankTransfer(e: FinanceEntry): boolean {
  const payer = norm((e as any).payer_name);
  const method = norm((e as any).payment_method);
  const category = norm((e as any).category);
  const note = norm((e as any).note);
  const text = `${method} ${category} ${note}`;
  if (payer) return true;
  return [
    "bank", "transfer", "ck", "chuyen khoan", "chuyển khoản",
    "ngan hang", "ngân hàng", "vietcombank", "vcb", "mb", "momo",
    "zalopay", "techcombank", "tcb", "bidv", "acb"
  ].some((w) => text.includes(w));
}
function financeNeedsMatchReview(e: FinanceEntry): boolean {
  const status = norm((e as any).match_status);
  if (status === "matched" || status === "ok" || status === "done") return false;
  if (!isIncomeLike(e)) return false;
  if (!isLikelyBankTransfer(e)) return false;
  if (status === "unmatched" || status === "pending" || status === "review" || status === "can kiem tra" || status === "cần kiểm tra") return true;
  // match_status="manual" là default nhập tay, không tự xem là chưa match.
  // Chỉ đưa vào danh sách CK cần kiểm tra nếu có dấu hiệu chuyển khoản và thiếu khách hoặc task.
  return !((e as any).customer_id && (e as any).task_id);
}
function financeMatchStatus(e: FinanceEntry): "matched" | "unmatched" | "pending" {
  if (!financeNeedsMatchReview(e)) return "matched";
  const status = norm((e as any).match_status);
  return status === "pending" || status === "review" || status === "manual" ? "pending" : "unmatched";
}

function Pill({ tone = "muted", children }: { tone?: "muted" | "primary" | "warn" | "ok" | "danger" | "amber"; children: ReactNode }) {
  const cls =
    tone === "primary" ? "border-primary/40 bg-primary/10 text-primary"
    : tone === "warn"  ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
    : tone === "amber" ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
    : tone === "ok"    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
    : tone === "danger"? "border-destructive/40 bg-destructive/10 text-destructive"
    : "border-border bg-muted text-muted-foreground";
  return <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] ${cls}`}>{children}</span>;
}

function CustomerBlock({ c, onOpen }: { c?: Customer | null; onOpen?: (c: Customer) => void }) {
  if (!c) return <span className="text-xs italic text-muted-foreground">Chưa có khách</span>;
  const real = String((c as any).real_name ?? "").trim();
  const banks = (((c as any).bank_transfer_names as string[] | null | undefined) ?? []).filter(Boolean);
  const tags = (((c as any).tags as string[] | null | undefined) ?? []).filter((t) => SHOW_TAGS.has(norm(t)));
  const phone = String((c as any).phone ?? "").trim();
  return (
    <div className="min-w-0">
      <button type="button" onClick={() => onOpen?.(c)} className="truncate text-left text-sm font-medium hover:underline">
        {c.name || "(không tên)"}
      </button>
      <div className="mt-0.5 flex flex-wrap items-center gap-1">
        {isVip(c) ? <Pill tone="primary">VIP</Pill> : null}
        {isPriority(c) ? <Pill tone="warn">Ưu tiên</Pill> : null}
        {tags.slice(0, 3).map((t, i) => (<Pill key={i}>{t}</Pill>))}
      </div>
      {real ? <p className="mt-0.5 truncate text-[11px] italic text-muted-foreground">Tên thật: {real}</p> : null}
      {banks.length > 0 ? (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {banks.slice(0, 2).map((b, i) => (<Pill key={i} tone="amber">CK: {b}</Pill>))}
          {banks.length > 2 ? <Pill>+{banks.length - 2}</Pill> : null}
        </div>
      ) : null}
      {phone ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{phone}</p> : null}
    </div>
  );
}

function StatusBadges({ a }: { a: ReturnType<typeof taskAmounts> }) {
  if (a.total <= 0) return <Pill>Chưa có giá</Pill>;
  return (
    <div className="flex flex-wrap gap-1">
      {a.paidEnough ? <Pill tone="ok">Đã tất toán</Pill> : null}
      {a.noDeposit ? <Pill tone="warn">Chưa cọc</Pill> : null}
      {a.partial ? <Pill tone="warn">Còn thiếu</Pill> : null}
    </div>
  );
}

export function DebtPaymentDashboard({
  currentStream,
  financeEntries,
  customers,
  financeLoading,
  financeError,
  onOpenCustomer,
}: {
  currentStream: Stream;
  financeEntries: FinanceEntry[];
  customers: Customer[];
  financeLoading?: boolean;
  financeError?: unknown;
  onOpenCustomer?: (c: Customer) => void;
}) {
  const stream = currentStream === "side" ? "side" : "main";
  const [tab, setTab] = useState<StatusTab>("needs-collect");
  const [q, setQ] = useState("");

  const taskProjectsQ = useQuery({
    queryKey: ["debt", "task-projects"],
    queryFn: () => api.get<TaskProject[]>("/tasks/projects"),
  });

  const projectStreamById = useMemo(() => {
    const m = new Map<string, Stream | null>();
    for (const p of taskProjectsQ.data ?? []) {
      const id = String(p.id ?? "").trim();
      if (id) m.set(id, projectToStream(p));
    }
    return m;
  }, [taskProjectsQ.data]);

  const taskProjectIdsKey = useMemo(
    () => (taskProjectsQ.data ?? []).map((p) => String(p.id ?? "").trim()).filter(Boolean).sort().join(","),
    [taskProjectsQ.data],
  );

  const tasksQ = useQuery({
    queryKey: ["debt", "tasks", "all-projects", taskProjectIdsKey],
    enabled: !!taskProjectsQ.data,
    queryFn: async () => {
      const ids = (taskProjectsQ.data ?? []).map((p) => String(p.id ?? "").trim()).filter(Boolean);
      const lists = await Promise.all(ids.map((project_id) => api.get<Task[]>("/tasks", { project_id }).catch(() => [] as Task[])));
      const merged = new Map<string, Task>();
      for (const list of lists) {
        for (const t of list) {
          if (t?.id) merged.set(String(t.id), t);
        }
      }
      return [...merged.values()];
    },
  });

  const customerMap = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of customers ?? []) m.set(c.id, c);
    return m;
  }, [customers]);

  // Map task_id → stream (để suy stream cho finance theo task liên kết).
  const taskStreamMap = useMemo(() => {
    const m = new Map<string, Stream | null>();
    for (const t of tasksQ.data ?? []) m.set(t.id, taskStream(t, projectStreamById));
    return m;
  }, [tasksQ.data, projectStreamById]);

  // Tasks có công nợ (total > 0 hoặc deposit > 0 hoặc payment_received).
  const debtTasksAll = useMemo(() => {
    return (tasksQ.data ?? []).filter((t) => {
      const a = taskAmounts(t);
      return a.total > 0 || a.deposit > 0 || (t as any).payment_received;
    });
  }, [tasksQ.data]);

  // Tasks cho stream đang chọn (loại bỏ unknown).
  const streamTasks = useMemo(
    () => debtTasksAll.filter((t) => taskStream(t, projectStreamById) === stream),
    [debtTasksAll, projectStreamById, stream],
  );
  // Task không xác định được dòng — chỉ để cảnh báo, không cộng tổng.
  const unknownTasks = useMemo(
    () => debtTasksAll.filter((t) => taskStream(t, projectStreamById) === null),
    [debtTasksAll, projectStreamById],
  );

  // Finance "CK cần kiểm tra" thuộc stream.
  const unmatchedCkStream = useMemo(() => {
    return (financeEntries ?? []).filter((e) => {
      const m = financeMatchStatus(e);
      if (m !== "unmatched" && m !== "pending") return false;
      return financeStream(e, taskStreamMap, projectStreamById) === stream;
    });
  }, [financeEntries, taskStreamMap, projectStreamById, stream]);

  // Finance "CK cần kiểm tra" không rõ dòng — panel cảnh báo riêng.
  const unmatchedCkUnknown = useMemo(() => {
    return (financeEntries ?? []).filter((e) => {
      const m = financeMatchStatus(e);
      if (m !== "unmatched" && m !== "pending") return false;
      return financeStream(e, taskStreamMap, projectStreamById) === null;
    });
  }, [financeEntries, taskStreamMap, projectStreamById]);

  // Totals cho stream đang chọn — TASK-ONLY.
  // Chỉ cộng "Tổng còn thu" cho task chưa tất toán; task đã tất toán vào KPI riêng.
  const totals = useMemo(() => {
    let noDep = 0, partial = 0, settled = 0, owedSum = 0, depositSum = 0;
    for (const t of streamTasks) {
      const a = taskAmounts(t);
      if (a.noDeposit) noDep++;
      if (a.partial) partial++;
      if (a.paidEnough) settled++;
      if (!a.paidEnough) owedSum += a.remaining;
      depositSum += a.deposit;
    }
    return { noDep, partial, settled, owedSum, depositSum, unmatched: unmatchedCkStream.length };
  }, [streamTasks, unmatchedCkStream.length]);

  const filtered = useMemo(() => {
    const needle = norm(q);
    return streamTasks.filter((t) => {
      const a = taskAmounts(t);
      const c = t.customer_id ? customerMap.get(t.customer_id) : null;
      if (tab === "needs-collect" && !(a.noDeposit || a.partial)) return false;
      if (tab === "no-deposit" && !a.noDeposit) return false;
      if (tab === "partial" && !a.partial) return false;
      if (tab === "settled" && !a.paidEnough) return false;
      if (tab === "vip" && (!(isVip(c) || isPriority(c)) || a.paidEnough)) return false;
      if (tab === "unmatched-ck") return false; // list riêng ở dưới
      if (!needle) return true;
      const hay = [
        t.title,
        (t as any).customer_quick_name,
        c?.name,
        (c as any)?.real_name,
        ...(((c as any)?.bank_transfer_names as string[] | undefined) ?? []),
      ].map(norm).join(" ");
      return hay.includes(needle);
    });
  }, [streamTasks, customerMap, tab, q]);

  const ckRows = useMemo(() => {
    const needle = norm(q);
    return unmatchedCkStream.filter((e) => {
      if (!needle) return true;
      const c = e.customer_id ? customerMap.get(e.customer_id) : null;
      const hay = [
        (e as any).payer_name, (e as any).payment_method, e.note, e.item_name,
        c?.name, (c as any)?.real_name,
      ].map(norm).join(" ");
      return hay.includes(needle);
    });
  }, [unmatchedCkStream, customerMap, q]);

  const loading = taskProjectsQ.isFetching || tasksQ.isFetching || !!financeLoading;
  const error = taskProjectsQ.error || tasksQ.error || financeError;
  const activeStream = STREAMS.find((s) => s.key === stream)!;

  return (
    <section className="space-y-4">
      {/* Dòng nghiệp vụ dùng stream có sẵn của FinancePage/web, không tạo selector riêng trong dashboard */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          title={activeStream.hint}
          className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs"
        >
          Đang xem: <b className="ml-1">{activeStream.label}</b>
        </span>
        <span className="text-[11px] text-muted-foreground">{activeStream.hint}</span>
      </div>

      {/* Tổng quan nhanh — chỉ trong stream đang chọn */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <KPI label="Chưa cọc"        value={String(totals.noDep)} tone="warn" />
        <KPI label="Còn thiếu"       value={String(totals.partial)} tone="warn" />
        <KPI label="Đã tất toán"     value={String(totals.settled)} tone="ok" />
        <KPI label="CK cần kiểm tra" value={String(totals.unmatched)} tone="amber" />
        <KPI label="Tổng còn thu"    value={formatMoney(totals.owedSum)} />
        <KPI label="Tổng cọc đã ghi" value={formatMoney(totals.depositSum)} />
      </div>

      {/* Status filters + search — trong stream */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`h-8 rounded-md border px-3 text-xs ${tab === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-md border border-border bg-card px-2">
          <Search className="h-3.5 w-3.5 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo khách / tên thật / tên CK / task…"
            className="h-8 w-56 bg-transparent text-xs outline-none"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          Không tải được dữ liệu công nợ. Nguồn lỗi có thể là /tasks/projects, /tasks?project_id, hoặc dữ liệu Finance/Customer từ tab Giao dịch.
        </div>
      ) : null}

      {/* Bảng công nợ theo Task (trong stream) */}
      {tab !== "unmatched-ck" ? (
        <div className="rounded-md border border-border bg-card">
          <SectionHeader title={`Công nợ theo Task — ${activeStream.label} (${filtered.length})`} loading={loading} />
          {filtered.length === 0 && !loading ? (
            <p className="p-4 text-sm text-muted-foreground">Không có task nào khớp bộ lọc trong {activeStream.label}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Khách</th>
                    <th className="px-3 py-2 font-medium">Task</th>
                    <th className="px-3 py-2 text-right font-medium">Tổng</th>
                    <th className="px-3 py-2 text-right font-medium">Cọc</th>
                    <th className="px-3 py-2 text-right font-medium">Còn lại</th>
                    <th className="px-3 py-2 font-medium">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const a = taskAmounts(t);
                    const c = t.customer_id ? customerMap.get(t.customer_id) : null;
                    const quickName = String((t as any).customer_quick_name ?? "").trim();
                    const due = (t as any).due_date || (t as any).return_date || (t as any).shoot_time;
                    return (
                      <tr key={t.id} className="border-t border-border align-top hover:bg-muted/40">
                        <td className="px-3 py-2">
                          {c ? (
                            <CustomerBlock c={c} onOpen={onOpenCustomer} />
                          ) : quickName ? (
                            <div>
                              <p className="truncate text-sm">{quickName}</p>
                              <Pill>Chưa liên kết khách</Pill>
                            </div>
                          ) : (
                            <span className="text-xs italic text-muted-foreground">Chưa có khách</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <p className="truncate text-sm">{t.title || "(không tên)"}</p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1">
                            {t.status ? <Pill>{t.status}</Pill> : null}
                            {due ? <span className="text-[11px] text-muted-foreground">{fmtDate(String(due))}</span> : null}
                            {a.paymentReceived ? <Pill tone="ok">Đã thu</Pill> : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{a.total > 0 ? formatMoney(a.total) : <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{a.deposit > 0 ? formatMoney(a.deposit) : <span className="text-muted-foreground">—</span>}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${a.remaining > 0 ? "text-amber-700 dark:text-amber-400" : "text-emerald-600"}`}>
                          {a.total > 0 ? formatMoney(a.remaining) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2"><StatusBadges a={a} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* CK cần kiểm tra — trong stream. Hiện trong default vì đây là việc cần xử lý. */}
      {(tab === "unmatched-ck" || tab === "needs-collect") ? (
        <div className="rounded-md border border-border bg-card">
          <SectionHeader title={`Giao dịch chuyển khoản cần kiểm tra — ${activeStream.label} (${ckRows.length})`} loading={loading} />
          {ckRows.length === 0 && !loading ? (
            <p className="p-4 text-sm text-muted-foreground">Không có giao dịch chuyển khoản cần kiểm tra trong {activeStream.label}.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Ngày</th>
                    <th className="px-3 py-2 font-medium">Tên người chuyển</th>
                    <th className="px-3 py-2 font-medium">Hình thức</th>
                    <th className="px-3 py-2 text-right font-medium">Số tiền</th>
                    <th className="px-3 py-2 font-medium">Liên kết</th>
                    <th className="px-3 py-2 font-medium">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {ckRows.slice(0, 200).map((e) => {
                    const c = e.customer_id ? customerMap.get(e.customer_id) : null;
                    const payer  = String((e as any).payer_name ?? "").trim();
                    const method = String((e as any).payment_method ?? "").trim();
                    const status = String((e as any).match_status ?? "").trim();
                    const tone = financeMatchStatus(e);
                    return (
                      <tr key={e.id} className="border-t border-border align-top hover:bg-muted/40">
                        <td className="px-3 py-2 text-xs">{e.occurred_on ? fmtDate(e.occurred_on) : "—"}</td>
                        <td className="px-3 py-2 text-sm">
                          {payer ? payer : <span className="text-xs italic text-muted-foreground">Chưa có</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{method || "—"}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${e.kind === "expense" ? "text-destructive" : "text-emerald-600"}`}>{formatMoney(Number(e.amount) || 0)}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {c ? <Pill tone="primary">Khách: {c.name}</Pill> : <Pill tone="warn">Chưa khách</Pill>}
                            {e.task_id ? <Pill tone="primary">Có task</Pill> : <Pill tone="warn">Chưa task</Pill>}
                            {tone === "pending" ? <Pill tone="amber">Cần kiểm tra</Pill> : null}
                            {status && tone !== "matched" ? <Pill>{status}</Pill> : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{e.note ? <span className="line-clamp-2">{e.note}</span> : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {ckRows.length > 200 ? (
                <p className="p-2 text-center text-[11px] text-muted-foreground">Hiển thị 200 dòng đầu. Lọc thêm bằng ô tìm kiếm.</p>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {/* Cảnh báo — bản ghi không xác định được dòng (KHÔNG cộng vào tổng) */}
      {(unknownTasks.length > 0 || unmatchedCkUnknown.length > 0) ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
          <p className="font-medium text-amber-700 dark:text-amber-400">
            Chưa rõ dòng — cần kiểm tra
          </p>
          <p className="mt-1 text-muted-foreground">
            Các bản ghi sau không nằm trong project chuẩn (rental-costume / making-props / photoshoot / rental-gun / order)
            nên KHÔNG được cộng vào tổng Dòng chính hay Dòng phụ. Mở Task hoặc Giao dịch để gán đúng project.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {unknownTasks.length > 0 ? <Pill tone="warn">{unknownTasks.length} task chưa rõ dòng</Pill> : null}
            {unmatchedCkUnknown.length > 0 ? <Pill tone="amber">{unmatchedCkUnknown.length} CK chưa rõ dòng</Pill> : null}
          </div>
        </div>
      ) : null}

      <p className="text-[11px] text-muted-foreground">
        Bảng chỉ đọc. Debt = TASK-only (không cộng finance vào tổng). Hai dòng (Dòng chính / Dòng phụ) không bao giờ cộng gộp.
      </p>
    </section>
  );
}

function KPI({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "amber" }) {
  const color =
    tone === "ok"   ? "text-emerald-600"
    : tone === "warn" ? "text-amber-700 dark:text-amber-400"
    : tone === "amber" ? "text-amber-700 dark:text-amber-400"
    : "";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`mt-1 text-base font-bold ${color}`}>{value}</p>
    </div>
  );
}
